import { supabase } from "@/integrations/supabase/client";

/**
 * Händelselogg — grunden för värde-dashboarden.
 * Loggningen är alltid "best effort": den får aldrig blockera eller krascha
 * det användaren faktiskt håller på med.
 */
export type UsageEventType =
  | "document_created"
  | "document_updated"
  | "document_exported"
  | "document_prefilled"
  | "ai_generation"
  | "share_sent"
  | "safety_round_completed"
  | "incident_reported"
  | "compliance_resolved"
  | "project_archived"
  | "project_exported"
  | "inspection_bundle_created"
  | "final_documentation_created";

export interface TrackOptions {
  projectId?: string | null;
  docNumber?: string | null;
  documentType?: string | null;
  durationMs?: number | null;
  metadata?: Record<string, unknown>;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export const resolveProjectIdFromUrl = (): string | null => {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(UUID_RE);
  return match ? match[0] : null;
};

export const trackUsage = async (
  eventType: UsageEventType,
  options: TrackOptions = {}
): Promise<void> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return;

    const projectId = options.projectId ?? resolveProjectIdFromUrl();

    await supabase.from("usage_events").insert({
      project_id: projectId,
      user_id: userId,
      event_type: eventType,
      doc_number: options.docNumber ?? null,
      document_type: options.documentType ?? null,
      duration_ms: options.durationMs ?? null,
      source: "app",
      metadata: (options.metadata ?? {}) as never,
    });
  } catch {
    /* tyst — mätning får aldrig störa användaren */
  }
};

/** Mäter hur lång tid en operation tar och loggar den. */
export const trackTimed = async <T,>(
  eventType: UsageEventType,
  fn: () => Promise<T>,
  options: TrackOptions = {}
): Promise<T> => {
  const start = performance.now();
  const result = await fn();
  void trackUsage(eventType, { ...options, durationMs: Math.round(performance.now() - start) });
  return result;
};
